// Script for re-using the marketplace header and footer in WebFlow.
// It's intended to be used as a "Code Embded", here is how the script is used in WebFlow:

/*
<script src="https://www.vendr.com/assets/external/ExternalMarketplace.elkZRZIh.js"></script>

<script>
  document.addEventListener("DOMContentLoaded", () => {
    ExternalMarketplace.init({
      footerNode: document.querySelector("footer"),
      headerNode: document.getElementById("embed-header"),
      styleSheetUrl:
        "https://www.vendr.com/assets/external/ExternalMarketplace.Dwifw7a9.css",
      segmentWriteKey: "GUz9AOqQpNEDLM0PpL1pg2uaiQ4Sl5sl",
      prefabApiKey:
        "426-Production-P454-E967-FRONTEND-bc23132b-0bd0-4d70-838b-0789aaf19c48",
      platformAppUrl: "https://app.vendr.com",
      algolia: {
        appId: "VYSCARNLSB",
        defaultIndex: "prod_backoffice",
        searchKey: "20d8ebb03c9f93edf1641cd97e5d970b",
      },
      identityV2Route: "https://www.vendr.com/api/identity/v2",
      areNewLandingPageLinksEnabled: true,
    });

      const header = document.getElementById("external-marketplace-header");
      const pageWrapper = header.closest(".page-wrapper").parentElement;
      pageWrapper.insertBefore(header, pageWrapper.firstChild);
  });
</script>

<div id="embed-header" style="height:80px"></div>
*/

import "~/globals.css";
import "./routes/_marketplace/marketplace.css";

import * as ReactDOM from "react-dom/client";
import { External } from "~/components/External";
import { Footer } from "~/components/Footer";
import { Header } from "~/components/Header";
import { SaasHeader } from "~/components/SaasHeader";
import * as AuthSchema from "~/routes/_marketplace.api.identity.v2/schema";

const lookupAuth = async (route?: string) => {
  try {
    const response = await fetch(route ?? "/api/identity/v2", {
      mode: "cors",
      credentials: "include",
    });

    if (!response.ok) {
      return AuthSchema.defaultResponse;
    }

    const body = await response.json();

    const result = AuthSchema.responseDecoder.safeParse(body);

    if (!result.success) {
      return AuthSchema.defaultResponse;
    }

    return result.data;
  } catch {
    return AuthSchema.defaultResponse;
  }
};

const renderInShadowDOM = (args: {
  target: Element;
  react: React.ReactNode;
  styleSheetUrl: string;
  segmentWriteKey?: string;
  prefabApiKey?: string;
  authPromise: Promise<AuthSchema.Response>;
  algolia?: {
    appId: string;
    defaultIndex: string;
    searchKey: string;
    productTierIndex: string;
  };
  stickyToTop?: boolean;
  zIndex?: string;
  id?: string;
}) => {
  const container = document.createElement("div");

  if (args.id) {
    container.id = args.id;
  }

  if (args.stickyToTop) {
    container.style.position = "sticky";
    container.style.top = "0px";
  }

  if (args.zIndex) {
    container.style.zIndex = args.zIndex;
  }

  args.target.replaceWith(container);

  const shadowRoot = container.attachShadow({ mode: "open" });

  const reactRoot = ReactDOM.createRoot(shadowRoot);

  reactRoot.render(
    <External
      styleSheetUrl={args.styleSheetUrl}
      algolia={args.algolia}
      authPromise={args.authPromise}
      prefabApiKey={args.prefabApiKey}
      segmentWriteKey={args.segmentWriteKey}
    >
      <link rel="stylesheet" href={args.styleSheetUrl} />

      <style>
        {`.radix-themes:where([data-is-root-theme='true']) {
          min-height: unset;
          background: none;
        }`}
      </style>

      {args.react}
    </External>,
  );
};

export type InitArgs = {
  bannerNode?: Element;
  headerNode?: Element;
  footerNode?: Element;
  styleSheetUrl: string;
  platformAppUrl: string;
  segmentWriteKey?: string;
  prefabApiKey?: string;
  algolia?: {
    appId: string;
    defaultIndex: string;
    searchKey: string;
    productTierIndex: string;
  };
  identityV2Route?: string;
};

const onMobileMenuClicked = () => {
  const banner = document.getElementById("external-marketplace-banner");

  if (banner) {
    const isHidden = banner.style.display === "none";
    banner.style.display = isHidden ? "block" : "none";
  }
};

export const init = (args: InitArgs) => {
  const authPromise = lookupAuth(args.identityV2Route);

  if (args.footerNode) {
    renderInShadowDOM({
      id: "external-marketplace-footer",
      target: args.footerNode,
      styleSheetUrl: args.styleSheetUrl,
      segmentWriteKey: args.segmentWriteKey,
      prefabApiKey: args.prefabApiKey,
      authPromise,
      algolia: args.algolia,
      zIndex: "0",
      react: <Footer />,
    });
  }

  if (args.bannerNode) {
    renderInShadowDOM({
      id: "external-marketplace-banner",
      target: args.bannerNode,
      styleSheetUrl: args.styleSheetUrl,
      segmentWriteKey: args.segmentWriteKey,
      prefabApiKey: args.prefabApiKey,
      authPromise,
      algolia: args.algolia,
      zIndex: "0",
      react: <SaasHeader />,
    });
  }

  if (args.headerNode) {
    renderInShadowDOM({
      id: "external-marketplace-header",
      target: args.headerNode,
      styleSheetUrl: args.styleSheetUrl,
      segmentWriteKey: args.segmentWriteKey,
      prefabApiKey: args.prefabApiKey,
      authPromise,
      algolia: args.algolia,
      stickyToTop: true,
      zIndex: "10",
      react: (
        <Header
          platformAppUrl={args.platformAppUrl}
          isMySuppliersEnabled={true}
          channelTargetOrigin={window.document.URL}
          onMobileMenuClicked={onMobileMenuClicked}
        />
      ),
    });
  }
};
